
"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Quote } from "@/lib/types";
import { Trash2, Tag, AlertCircle } from "lucide-react";

interface QuoteListProps {
  quotes: Quote[];
  onDeleteQuote: (id: string) => void;
}

export default function QuoteList({
  quotes,
  onDeleteQuote,
}: QuoteListProps) {
  const total = quotes.reduce((acc, quote) => acc + (quote.price || 0), 0);

  return (
    <Card className="shadow-lg h-full flex flex-col">
      <CardHeader>
        <CardTitle className="font-headline flex items-center gap-2">
          <Tag className="w-6 h-6 text-primary" />
          Cotizaciones Guardadas
        </CardTitle>
        <CardDescription>
          Aquí puedes ver las cotizaciones que has generado.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col p-0 pb-4">
        <ScrollArea className="flex-grow px-6">
          {quotes.length === 0 ? (
            <div className="flex items-center justify-center h-full text-center text-muted-foreground min-h-[200px]">
              <p>No has guardado ninguna cotización.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {quotes.map((quote) => (
                <li
                  key={quote.id}
                  className="p-4 bg-secondary rounded-lg"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">
                        {quote.brand} {quote.model}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {quote.repair_type}
                        {quote.screen_quality && ` (Calidad: ${quote.screen_quality})`}
                      </p>
                      {quote.email && (
                        <p className="text-xs text-muted-foreground italic pt-1">
                          Email: {quote.email}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-1">
                    {quote.price !== undefined ? (
                        <p className="font-bold text-lg text-primary">
                          ${quote.price.toLocaleString("es-MX")}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-accent-foreground bg-accent/30 py-1 px-3 rounded-full">
                          <AlertCircle className="w-4 h-4"/>
                          <span>Contactar</span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => onDeleteQuote(quote.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="sr-only">Eliminar cotización</span>
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
        {quotes.length > 0 && (
          <div className="px-6 pt-4 border-t mt-4">
            <div className="flex justify-between items-center font-bold text-xl">
              <span>Total Estimado:</span>
              <span className="text-primary">${total.toLocaleString("es-MX")}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Estos precios son estimados y están sujetos a cambios tras el diagnóstico del técnico. Para registrar su dispositivo, por favor acuda a una sucursal.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
